import { createPortal } from "react-dom";

// Help modal component with comprehensive DSL guide
export const PathwayDslHelpModal = ({ onClose }: { onClose: () => void }) => {
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 12,
          maxWidth: 800,
          maxHeight: "90vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Pathway DSL Guide
          </h2>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              border: "none",
              background: "#e5e7eb",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 18,
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: 24,
            fontSize: 14,
            lineHeight: 1.6,
            color: "#374151",
          }}
        >
          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Overview
            </h3>
            <p style={{ marginBottom: 12 }}>
              The Pathway DSL (Domain Specific Language) lets you define
              patterns to find patients who followed specific journeys through
              the Mental Health Act. Think of it like a search pattern that
              matches sequences of detention periods.
            </p>
            <p>
              A pathway pattern consists of a <strong>name</strong>, a sequence
              of <strong>steps</strong>
              to match, and optional <strong>measurements</strong> to calculate.
            </p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Basic Structure
            </h3>
            <p style={{ marginBottom: 12 }}>
              A pattern file has three sections separated by <code>---</code>:
            </p>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
              }}
            >{`Pattern Name
---
step1
step2
step3
---
measure "Name": step1 -> step2`}</pre>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Matching MHA Sections
            </h3>
            <p style={{ marginBottom: 12 }}>
              Each step matches a detention period. Available statuses (full and
              compact forms):
            </p>
            <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
              <li>
                <code>Section 2</code> or <code>2</code> or <code>s2</code> -
                Assessment order (28 days)
              </li>
              <li>
                <code>Section 3</code> or <code>3</code> or <code>s3</code> -
                Treatment order (6 months)
              </li>
              <li>
                <code>Section 4</code> or <code>4</code> or <code>s4</code> -
                Emergency admission (72 hours)
              </li>
              <li>
                <code>Section 5(2)</code> or <code>5(2)</code> - Doctor&apos;s
                holding power (72 hours)
              </li>
              <li>
                <code>Section 5(4)</code> or <code>5(4)</code> - Nurse&apos;s
                holding power (6 hours)
              </li>
              <li>
                <code>Section 17A</code> or <code>17A</code> or <code>CTO</code>{" "}
                - Community Treatment Order
              </li>
              <li>
                <code>Section 17A (Recalled)</code> or <code>17A-R</code> or{" "}
                <code>CTO-R</code> - Patient recalled from CTO (max 72 hours)
              </li>
              <li>
                <code>Not Detained</code> or <code>ND</code> - Not under
                detention (includes &quot;Expired&quot;)
              </li>
            </ul>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Pattern
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Matches
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Section 2
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Exactly &quot;Section 2&quot;
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Not Detained
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Period when patient is not detained (also matches
                    &quot;Expired&quot;)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    2 | 3
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Either Section 2 OR Section 3 (pipe as OR)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    2, 3
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Either Section 2 OR Section 3 (comma as OR)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    *
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Any section (wildcard)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    !3
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Any section EXCEPT Section 3 (implicit wildcard)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    !2, !3
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Any section except Section 2 or 3
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>
                    * !2, !3
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    Same as above (explicit wildcard, backward compatible)
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Duration Constraints
            </h3>
            <p style={{ marginBottom: 12 }}>
              Add duration requirements after the section name using{" "}
              <code>d</code> for days:
            </p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Pattern
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Meaning
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >{`Section 2 >= 7d`}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    At least 7 days
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >{`Section 2 <= 28d`}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    At most 28 days
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >{`Section 2 >= 7d <= 28d`}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Between 7 and 28 days
                  </td>
                </tr>
                <tr>
                  <td
                    style={{ padding: "8px 12px", fontFamily: "monospace" }}
                  >{`Not Detained 7d-28d`}</td>
                  <td style={{ padding: "8px 12px" }}>
                    Range: 7 to 28 days (alternative syntax)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderTop: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >{`Section 2 > 7d`}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    More than 7 days (strictly greater)
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontFamily: "monospace",
                    }}
                  >{`Section 2 < 28d`}</td>
                  <td
                    style={{
                      padding: "8px 12px",
                    }}
                  >
                    Less than 28 days (strictly less)
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Quantifiers (Repetition)
            </h3>
            <p style={{ marginBottom: 12 }}>
              Control how many times a step can match, similar to regex:
            </p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Quantifier
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Meaning
                  </th>
                  <th
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Example
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    (none)
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Exactly one
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Section 2
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    ?
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Zero or one (optional)
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Section 4 ?
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    +
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    One or more
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Section 2 +
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    *
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Zero or more
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    * !Section 3 *
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    ??
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    Zero or one (non-greedy)
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    Section 4 ??
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    +?
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    One or more (non-greedy)
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      fontFamily: "monospace",
                    }}
                  >
                    * +?
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>
                    *?
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    Zero or more (non-greedy)
                  </td>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>
                    * !Section 3 *?
                  </td>
                </tr>
              </tbody>
            </table>
            <p style={{ fontSize: 13, color: "#6b7280" }}>
              <strong>Greedy vs Non-greedy:</strong> Greedy quantifiers match as
              much as possible, non-greedy match as little as possible. Use
              non-greedy (*?) when you want to find the shortest matching
              sequence.
            </p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Step IDs for Measurements
            </h3>
            <p style={{ marginBottom: 12 }}>
              Add <code>@id</code> to name a step for use in measurements:
            </p>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
              }}
            >{`Section 2 >= 7d @initial
Not Detained @release
Section 2 @readmit`}</pre>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Measurements
            </h3>
            <p style={{ marginBottom: 12 }}>
              Define measurements in the third section to calculate time between
              steps:
            </p>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
                marginBottom: 12,
              }}
            >{`measure "Time to readmission": release -> readmit
measure "Initial detention length": initial.start -> initial.end`}</pre>
            <p style={{ fontSize: 13, color: "#6b7280" }}>
              Use <code>.start</code> and <code>.end</code> to specify which
              edge of a step to measure from/to. Default is <code>.start</code>.
            </p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Complete Examples
            </h3>

            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
                marginTop: 16,
              }}
            >
              Section 2 Readmission within 28 days
            </h4>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
                marginBottom: 8,
              }}
            >{`Section 2 Readmission
---
Section 2 >= 7d @initial
Not Detained @gap
Section 2 @readmit
---
measure "Gap duration": gap.start -> gap.end
measure "Initial stay": initial.start -> initial.end`}</pre>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Finds patients detained under Section 2 for at least 7 days,
              released, then readmitted within 28 days.
            </p>

            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              Section 2 to Section 3 Conversion
            </h4>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
                marginBottom: 8,
              }}
            >{`Section 2 to Section 3 Conversion
---
Section 2 @s2
Section 3 @s3
---
measure "Time to conversion": s2.start -> s3.start`}</pre>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Finds patients who were on Section 2, then converted to Section 3.
            </p>

            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              Holding Power to Full Section
            </h4>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
                marginBottom: 8,
              }}
            >{`Holding Power to Full Section
---
Section 5(4) | Section 5(2) @holding
Section 2 | Section 3 @fullSection
---
measure "Time to full section": holding.start -> fullSection.start`}</pre>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Finds patients who started on a holding power (5(4) or 5(2)) and
              progressed to a full section (2 or 3).
            </p>

            <h4
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              CTO Recall to Revocation
            </h4>
            <pre
              style={{
                background: "#f3f4f6",
                padding: 16,
                borderRadius: 8,
                overflow: "auto",
                fontSize: 13,
                fontFamily: "monospace",
                marginBottom: 8,
              }}
            >{`CTO Recall to Revocation
---
CTO @cto
CTO (Recalled) @recall
Section 3 @revoked
---
measure "CTO to recall": cto.start -> recall.start
measure "Recall duration": recall.start -> recall.end`}</pre>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Finds patients on CTO who were recalled (CTO4 form) and then had
              their CTO revoked (placed on Section 3). Recall periods are capped
              at 72 hours. <code>CTO</code> is an alias for{" "}
              <code>Section 17A</code>.
            </p>
          </section>

          <section>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Tips
            </h3>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Start simple:</strong> Begin with exact section matches,
                then add duration constraints and quantifiers as needed.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Use comments:</strong> Lines starting with{" "}
                <code>#</code> are ignored.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Test iteratively:</strong> The match count updates as
                you type, so you can see how changes affect results.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Save useful patterns:</strong> Click &quot;Save
                Current&quot; to keep patterns for future use.
              </li>
              <li>
                <strong>
                  &quot;Not Detained&quot; includes &quot;Expired&quot;:
                </strong>{" "}
                When matching &quot;Not Detained&quot;, patients whose detention
                expired are also matched.
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
};
